import React from 'react';

import { DashboardHeaderLayoutComponent } from 'design/dashboardHeaderLayout';
import { DashboardNavigationControlComponent } from 'design/dashboardNavigationLinkControl';
import { LinkControlComponent } from 'design/linkControl';
import { TextComponent } from 'design/text/textComponents';
import { LogoComponent } from 'design/logo';
import { InlineLayout } from 'design/inlineLayout';
import { routes } from 'utils/routes';
import { useIntlDictionary } from 'hooks/useIntlDictionary';
import { useUserPartnerCode } from 'hooks/useUserPartnerCode';
import { useCancelledClientsCount } from 'hooks/useCancelledClientsCount';
import { CopyPartnerCodeButton } from 'containers/CopyPartnerCodeButton';
import { AvatarComponent } from 'design/avatar';
import { useUserPartnerProfile } from 'hooks/useUserPartnerProfile';
import { ClickControlComponent } from 'design/clickControl/clickControlComponents';
import { RoundPressControlComponent } from 'design/roundPressControl';
import { useUserPartnerBalance } from 'hooks/useUserPartnerBalance';

type Props = {
  onManageProfile(): void;
  onManageFinances(): void;
};

export const PartnerDashboardHeader = (props: Props) => {
  const {
    onManageProfile: manageProfile,
    onManageFinances: manageFinances,
  } = props;

  const {
    partnerDashboard: { header: ownDictionary },
  } = useIntlDictionary();

  const partnerCode = useUserPartnerCode();

  const balance = useUserPartnerBalance();

  const cancelledClientsCount = useCancelledClientsCount();

  const partnerProfile = useUserPartnerProfile();

  const navigationControlsNode = React.useMemo(
    () => (
      <>
        <DashboardNavigationControlComponent to={routes.clientsActive()}>
          <TextComponent>{ownDictionary.activeClientsLinkText}</TextComponent>
        </DashboardNavigationControlComponent>
        <DashboardNavigationControlComponent
          badgeNumber={cancelledClientsCount}
          to={routes.clientsCancelled()}
        >
          <TextComponent>
            {ownDictionary.clientsCancelledLinkText}
          </TextComponent>
        </DashboardNavigationControlComponent>
      </>
    ),
    [
      cancelledClientsCount,
      ownDictionary.activeClientsLinkText,
      ownDictionary.clientsCancelledLinkText,
    ]
  );

  const accountControlsNode = React.useMemo(
    () => (
      <>
        <InlineLayout gap="8px">
          <TextComponent>{ownDictionary.partnersCodeLabel}</TextComponent>
          <CopyPartnerCodeButton
            partnerCode={partnerCode}
          ></CopyPartnerCodeButton>
        </InlineLayout>
        <InlineLayout gap="8px">
          <TextComponent>{ownDictionary.balanceLabel}</TextComponent>
          <RoundPressControlComponent onPress={manageFinances}>
            {ownDictionary.financesManagerActionText(balance)}
          </RoundPressControlComponent>
        </InlineLayout>
        {partnerProfile.avatarURL && (
          <ClickControlComponent onClick={manageProfile}>
            <AvatarComponent
              size="s"
              url={partnerProfile.avatarURL}
              alt="my avatar"
            />
          </ClickControlComponent>
        )}
      </>
    ),
    [
      balance,
      manageFinances,
      manageProfile,
      ownDictionary,
      partnerCode,
      partnerProfile.avatarURL,
    ]
  );

  return (
    <DashboardHeaderLayoutComponent
      logo={
        <LinkControlComponent to="/" local>
          <TextComponent color="primary" size="l">
            <LogoComponent />
          </TextComponent>
        </LinkControlComponent>
      }
      navigationControls={navigationControlsNode}
      accountControls={accountControlsNode}
    />
  );
};
